/**
 * Число, що «перекручується» до нового значення, як на лічильнику.
 *
 * Потрібне там, де одні й ті самі показники перераховуються при зміні
 * контексту (перемикання доби D-2 / D-1 / Сьогодні на /forecast): миттєва
 * підміна цифри читається як збій, а плавний перехід показує, що це те саме
 * поле змінило значення.
 *
 * ДВІ ПАСТКИ, ЧЕРЕЗ ЯКІ АНІМАЦІЇ МОЖЕ НЕ БУТИ ВИДНО:
 *
 * 1. Перемонтування. Якщо React знищує елемент (змінився key, батько сховав
 *    його за умовою), внутрішній стан губиться й компонент стартує одразу з
 *    кінцевим числом. Тому останнє показане значення кешується за `id` на
 *    рівні модуля: навіть після перемонтування анімація починається звідти,
 *    де користувач реально бачив число.
 *
 * 2. `prefers-reduced-motion`. Windows Server та інші системи з вимкненими
 *    ефектами повідомляють «зменшити анімації», і будь-який захист за цією
 *    ознакою глушив би ефект повністю. Тут ця перевірка свідомо НЕ робиться:
 *    лічильник — явна продуктова вимога, а не декоративна дрібниця. Якщо
 *    колись знадобиться повага до системного налаштування, це слід робити
 *    перемикачем у налаштуваннях кабінету, а не мовчазним відключенням.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';

interface Props {
  value: number | null | undefined;
  /** Форматер кінцевого рядка — той самий, що й для статичного значення. */
  format: (v: number) => string;
  /**
   * Стабільний ключ показника (напр. 'forecast', 'base'). Потрібен, щоб
   * пережити перемонтування: без нього анімація після нього не відтвориться.
   */
  id?: string;
  durationMs?: number;
  className?: string;
  style?: CSSProperties;
}

// Останнє ПОКАЗАНЕ значення кожного показника. Живе поза React, тож не
// залежить від життєвого циклу компонента.
const lastShown = new Map<string, number>();

// easeOutCubic: швидкий старт і м'яке гальмування — на лічильнику читається
// природніше за лінійний хід.
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export default function AnimatedNumber({ value, format, id, durationMs = 800, className, style }: Props) {
  const initial = id != null && lastShown.has(id) ? lastShown.get(id)! : (value ?? null);
  const [display, setDisplay] = useState<number | null>(initial);
  // Те, що зараз РЕАЛЬНО на екрані: якщо анімацію перервали на півдорозі,
  // наступна стартує звідти, а не від застарілого значення.
  const shownRef = useRef<number | null>(initial);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const target = value ?? null;
    const from = shownRef.current;

    const settle = (v: number | null) => {
      shownRef.current = v;
      if (id != null && v != null) lastShown.set(id, v);
      setDisplay(v);
    };

    // Анімується лише перехід ЧИСЛО → ЧИСЛО. Якщо з будь-якого боку даних
    // немає, «перекручувати» нема від чого — це був би стрибок від нуля.
    if (target == null || from == null || from === target) {
      settle(target);
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const v = from + (target - from) * easeOutCubic(t);
      if (t < 1) {
        shownRef.current = v;
        if (id != null) lastShown.set(id, v);
        setDisplay(v);
        rafRef.current = requestAnimationFrame(tick);
      } else {
        settle(target);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value, durationMs, id]);

  return (
    <span className={className} style={style}>
      {display == null ? '—' : format(display)}
    </span>
  );
}
